import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { getRequest as get, postRequest as post } from '../utils/HttpMethods';

import { EOB, isEOB } from 'interfaces';
import type { Province } from '@billing/validation/payor';

import {
  createEntity,
  updateEntity,
  useCountQuery,
  useListQuery,
  useMutationQuery,
  CountReturnTypes,
} from '../utils/queries';

export const eobQueryKey = {
  all: ['eobs'],
  list: () => [...eobQueryKey.all, 'list'],
};

/**
 * @description Fetches a list of all EOBs.
 * @returns {Promise<EOB[]>} A promise that resolves to an array of EOBs.
 */
const listEobs = async (): Promise<EOB[]> =>
  await get<EOB[]>('QBilling', `/resource/eob/all`);

/**
 * @description Creates a new EOB.
 * @param {EOB} eob - The EOB to create.
 * @returns {Promise<EOB>} A promise that resolves to the created EOB.
 */
const createEob = async (eob: EOB): Promise<EOB> => {
  return createEntity<EOB>({
    entity: eob,
    functionName: 'createEob',
    isEntity: isEOB,
    path: '/resource/eob',
  });
};

/**
 * @description Updates an existing EOB.
 * @param {EOB} eob - The EOB to update.
 * @returns {Promise<EOB>} A promise that resolves to the updated EOB.
 */
const updateEob = async (eob: EOB): Promise<EOB> => {
  return updateEntity<EOB>({
    entity: eob,
    functionName: 'updateEob',
    isEntity: isEOB,
    path: '/resource/eob',
  });
};

const getEOBCount = async (): Promise<Record<string, number>> => {
  return await get<Record<string, number>>('QBilling', `/resource/eob/count`);
};
export const useGetEOBCount = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: [...eobQueryKey.all, 'count'],
    fetchFunction: getEOBCount,
    functionName: 'getEOBCount',
    enabled,
    params: {},
  });
};

/**
 * @description Bulk ingests EOBs from a PDF file.
 * @param {File} eobPDF - The PDF file containing EOBs.
 * @returns {Promise<EOB[]>} A promise that resolves to an array of EOBs.
 */
export const bulkIngestEOBs = async (
  eobPDF: File,
  province: Province
): Promise<string> => {
  const bucket = `qbilling-eob-files`;
  const key = `${eobPDF.name}`;
  const bulkRequest: { message: string } = await post(
    'QBilling',
    `/resource/eob/bulk`,
    {
      key,
      bucket,
      province,
    }
  );
  const parsedRequest = await bulkRequest.message;
  if (!parsedRequest || !Object.keys(parsedRequest).length) {
    return '';
  }
  return parsedRequest;
};

/**
 * @description Hook to fetch a list of EOBs.
 * @param {Object} params - The parameters for the hook.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<EOB[], Error>} The query result containing the list of EOBs.
 */
export const useListEobs = ({
  displayNotification = false,
  enabled = true,
}: {
  displayNotification?: boolean;
  enabled?: boolean;
}): UseQueryResult<EOB[], Error> => {
  return useListQuery<EOB>({
    queryKey: eobQueryKey.list(),
    fetchFunction: listEobs,
    functionName: 'listEobs',
    isEntity: isEOB,
    enabled,
    displayNotification: displayNotification,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * @description Hook to create a new EOB.
 * @returns {UseMutationResult<EOB, Error>} The mutation result for creating an EOB.
 */
export const useCreateEob = (): UseMutationResult<EOB, Error> => {
  return useMutationQuery<EOB>({
    errorMessage: 'Error Creating EOB',
    mutationFn: createEob,
    queryKey: eobQueryKey.all,
    successMessage: 'EOB created successfully',
  });
};

/**
 * @description Hook to update an existing EOB.
 * @returns {UseMutationResult<EOB, Error>} The mutation result for updating an EOB.
 */
export const useUpdateEob = (): UseMutationResult<EOB, Error> => {
  return useMutationQuery<EOB>({
    errorMessage: 'Error Updating EOB',
    mutationFn: updateEob,
    queryKey: eobQueryKey.all,
    successMessage: 'EOB updated successfully',
  });
};
